/**
 * StartAheadHost — "Start ahead" (docs/COMPRESSED_CHAIN.md, Matt 2026-10-29).
 *
 * A milestone waiting on a predecessor can begin early only on a firm leader's call, named and with a
 * reason. The predecessor LINK STAYS (the schedule keeps its order and the reserve math stays honest);
 * the call is what stops it holding the milestone back. Before this the only way to start early was to
 * delete the link, which left the schedule waiting on nothing (Santiago's DAPC, 2026-10-22).
 *
 * Opens on `open-start-ahead` { msId, predId?, fromGantt?, onUnlink? }:
 *   · from the milestone's ⋯ menu (a leader on a waiting milestone),
 *   · from the Gantt, when someone unlinks a predecessor that hasn't posted: the window asks which it
 *     is — start ahead (keeps the link) or the link was wrong (removes it, through onUnlink).
 * Mounted once in OutlineView.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import TriageTiles from './TriageTiles.jsx'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { hasModule } from '../services/tenancy.js'
import { startAhead } from '../services/milestoneFlow.js'
import { isStartedAhead } from '../outline/milestoneFlow.js'

function findMs(id) {
  const lists = [ctx.state && ctx.state.milestones]
  if (ctx.milestonesCache) for (const k of Object.keys(ctx.milestonesCache)) lists.push(ctx.milestonesCache[k])
  for (const l of lists) if (Array.isArray(l)) for (const m of l) if (m && m.id === id) return m
  return null
}
const nameOf = (rec, fallback) => {
  const f = (rec && rec.fields) || {}
  return String(f['Full Deliverable Name'] || f['Name'] || fallback || 'the milestone').replace(/\s*🔄\s*/g, ' ').trim()
}

export default function StartAheadHost() {
  const [req, setReq] = useState(null)   // { msId, predId?, fromGantt?, onUnlink?, msName?, predName? }
  useEffect(() => {
    const on = (e) => { const d = e.detail || {}; if (d.msId) setReq(d) }
    window.addEventListener('open-start-ahead', on)
    return () => window.removeEventListener('open-start-ahead', on)
  }, [])
  if (!req) return null

  const close = () => { setReq(null); window.dispatchEvent(new Event('outline-render')) }
  const ms = findMs(req.msId)
  const msName = req.msName || nameOf(ms)
  // The predecessors still open: one named in the request, else every unposted, not-yet-started-ahead link.
  const preds = req.predId
    ? [{ id: req.predId, name: req.predName || nameOf(findMs(req.predId), 'the predecessor') }]
    : ((ms && ms.fields && ms.fields['Predecessor']) || [])
        .map(id => ({ id, rec: findMs(id) }))
        .filter(p => (!p.rec || !p.rec.fields['Posted Date']) && !isStartedAhead(req.msId, p.id))
        .map(p => ({ id: p.id, name: nameOf(p.rec, 'a predecessor') }))
  const leader = hasModule('workload') || hasModule('admin')
  // Starting ahead is meant to be rare (AlderFLOW rule 24): the firm's count for the last 7 days is
  // shown to whoever is about to make the call.
  const weekAgo = Date.now() - 7 * 864e5
  const thisWeek = Object.values((ctx.milestoneFlow && ctx.milestoneFlow.aheadByMs) || {}).flat()
    .filter(r => r.created_at && new Date(r.created_at).getTime() >= weekAgo).length
  const f = (ctx.currentMember && ctx.currentMember.fields) || {}
  const me = { id: wf.currentMemberId || null, name: f['Member Name'] || f['Name'] || '' }

  const options = preds.map((p, i) => ({
    key: 'ahead:' + p.id, icon: 'escalate', tone: i === 0 ? 'primary' : undefined,
    label: preds.length > 1 ? 'Start ahead of ' + p.name : 'Start ahead',
    sub: leader ? 'Keep the link. It can begin now.' : 'A firm leader makes this call',
    disabled: !leader, title: leader ? undefined : 'Only someone who runs the Flight Board can start work ahead.',
    step: {
      title: 'Start ' + msName + ' ahead of ' + p.name,
      body: 'The link stays, so the schedule keeps its order. It shows on the milestone as started ahead, with your name and the reason, and the kit records anything that ' + p.name + ' would have provided as went ahead without it, until it really arrives.',
      kind: 'text', placeholder: 'Why it can’t wait, in a sentence',
      confirm: () => 'Start it ahead', confirmIcon: 'escalate',
    },
    run: async (reason) => {
      const r = await startAhead({ milestoneId: req.msId, predecessorId: p.id, reason, memberId: me.id, memberName: me.name })
      return r
    },
    done: (r) => 'Started ahead of ' + p.name + '. It can be crewed now.' + (r && r.kitMarked && r.kitMarked.length ? ' The kit shows ' + r.kitMarked.length + ' item' + (r.kitMarked.length === 1 ? '' : 's') + ' as went ahead without it.' : ''),
  }))
  if (req.fromGantt && req.onUnlink) {
    options.push({
      key: 'unlink', icon: 'notreq', tone: 'caution', label: 'The link was wrong',
      sub: 'It never depended on it. Remove the link.',
      step: { title: 'Remove the link', body: msName + ' will no longer wait on ' + (preds[0] ? preds[0].name : 'it') + ', and the schedule will stop ordering them. Save the schedule to keep it.', kind: 'confirm', confirm: () => 'Remove the link' },
      run: async () => { req.onUnlink(); return null },
      done: () => 'Link removed. Save the schedule to keep it.',
    })
  }
  options.push({ key: 'wait', icon: 'clock', label: 'Keep waiting', sub: 'Nothing changes', open: close })

  return createPortal(
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 1000010, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(620px, 100%)', background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, padding: '20px 24px', boxShadow: '0 20px 60px rgba(7,39,45,0.3)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text, #07272D)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted, #7a9aaa)' }}>{msName}</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
              {preds.length ? 'Waiting on ' + preds.map(p => p.name).join(' and ') : 'Nothing is holding this milestone back.'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--aj-text-muted, #7a9aaa)', marginTop: 4 }}>
              {thisWeek === 0 ? 'Nothing has been started ahead in the last 7 days.' : thisWeek + ' milestone' + (thisWeek === 1 ? ' was' : 's were') + ' started ahead in the last 7 days.'}
            </div>
          </div>
          <button onClick={close} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--aj-text-muted, #7a9aaa)' }}>×</button>
        </div>
        {preds.length > 0 && (
          <TriageTiles options={options}
            question={req.fromGantt ? 'Is the link wrong, or does this need to start before it posts?' : 'Start it before ' + (preds.length === 1 ? 'that posts' : 'they post') + '?'}
            onConfirm={async (key, v) => { const o = options.find(x => x.key === key); const r = await o.run(v); return o.done(r) }}
            onFolded={close} />
        )}
      </div>
    </div>,
    document.body
  )
}
